import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import SpinnerLoader from "../../../Component/shared/loader/spinnerLoader/SpinnerLoader";
import { getSessionStorage } from "../../../helpers/storage";
import { CurrentProviderData, IntakeSheetSession,PregnancyConsents as PregnancyConsentKeys, } from "../../../Constant";
import { GetCaseFormListData, SaveCaseFormData } from "../Service";
import toast from "react-hot-toast";
import { GragIntakeSheetData } from "../IntakeSheetUtils";
import { GetPatientDetails } from "../../patientDetail/Service";
import moment from "moment";
import Select from "@mui/material/Select";
import DatePicker from "react-datepicker";
import SignaturePad from "../../../Component/shared/signaturePad";
import "./PregnancyConsent.css";
const PregnancyConsent = forwardRef(function PregnancyConsent(props, ref) {
  const {
    providerOptions = [],
    sessionData = {},
    signatureMiss,
    copySignature,
  } = props;

  const tokenData = JSON.parse(sessionStorage.getItem("tokenData") || "{}");
  const providerData = getSessionStorage(CurrentProviderData) || [];
  const roleName = sessionData?.Role?.Name?.trim().toLowerCase() ?? "";
  const isFrontDesk =
    roleName === "frontdesk" || roleName === "front desk" || roleName === "admin";

  /* ── state ── */
  const [loading, setLoading]                   = useState(false);
  const [CaseFormDataId, setCaseFormDataId]     = useState("");   // ← capital C, like HippaAuth
  const [collectionOfPC, setCollectionOfPC]     = useState([]);
  const [BeforeParsingPC, setBeforeParsingPC]   = useState([]);  // ← capital B, like HippaAuth

  const [patientName, setPatientName]           = useState("");
  const [formDate, setFormDate]                 = useState(new Date());
  const [providerName, setProviderName]         = useState("");
  const [selectedProvider, setSelectedProvider] = useState(providerOptions[0] || null);
  const [providerAddress, setProviderAddress]   = useState("");
  const [patientSignImg, setPatientSignImg]     = useState("");
  const [sigPadOpen, setSigPadOpen]             = useState(false);

  /* ── imperative handle ── */
  useImperativeHandle(ref, () => ({
    save:           () => saveFunction(),
    isPending:      () => checkIsPending(),
    getSignature: () => patientSignImg || "",   
    applySignature: (sig) => {
      if (!sig) return;
      setPatientSignImg(sig);
    },
  }), [patientSignImg]);

  const checkIsPending = () => {
    if (!patientSignImg) return true;
    return false;
  };

  useEffect(() => {
    props?.onReady?.("preg_consent");
  }, [patientSignImg]);

  useEffect(() => {
    callApi();
  }, []);

  /* ══════════════════════════════════════════════════
     FETCH
  ══════════════════════════════════════════════════ */
  const handleGetCaseFormData = async () => {
    try {
      const requestBody = {
        CaseFormDataId:  null,
        CaseId:          tokenData.caseId,
        CompanyId:       tokenData.companyId,
        DoctorId:        "ALL",
        EntityLevel:     0,
        FormId:          23,
        FormName:        "Pregnancy Consent",
        FormType:        null,
        IsFormCompleted: false,
        JsonData:        null,
        TemplateName:    null,
        UserId: "US000000000000012172",
      };
      const response = await GetCaseFormListData(requestBody);
      if (response?.HasException) {
        toast.error(`Something went wrong. ${response?.MessageText}. ${response?.Exception}`);
      } else {
        const data = response?.Data[0] || {};
        return data;
      }
    } catch (error) {
      toast.error(error.message);
      return {};
    }
  };

  const callApi = async () => {
    setLoading(true);
    const formData    = await handleGetCaseFormData();
    const patientInfo = await handleGetPatientDetails();

    if (!formData?.JsonData) {
      console.error("No JsonData returned from GetCaseFormListData");
      setLoading(false);
      return;
    }

    const pcData     = JSON.parse(formData.JsonData);
    const collection = GragIntakeSheetData(pcData);

    setCollectionOfPC(collection);
    setBeforeParsingPC(pcData);
    setCaseFormDataId(formData.CaseFormDataId);   // ← no fallback "", same as HippaAuth

    bindValueToControl(collection, patientInfo);
    setLoading(false);
  };

  const handleGetPatientDetails = async () => {
    try {
      const caseId    = tokenData?.caseId;
      const companyId = tokenData?.companyId;
      if (!caseId || !companyId) {
        toast.error("Something went wrong while fetching your details.");
        return {};
      }
      const response = await GetPatientDetails(caseId, companyId);
      if (response?.success && response?.data) {
        return response.data;
      }
      toast.error("Something went wrong while fetching your details.");
      return {};
    } catch (error) {
      console.error("handleGetPatientDetails error:", error);
      toast.error("Something went wrong while fetching your details.");
      return {};
    }
  };

  /* ══════════════════════════════════════════════════
     BIND VALUES
  ══════════════════════════════════════════════════ */
  const bindValueToControl = (collection, patientInfo) => {
    if (!patientInfo) return;

    const getValue = (key) => {
      const found = collection.find((x) => x.Name === key);
      return found ? found.Value : "";
    };

    const parseDateSafe = (raw) =>
      isNaN(new Date(raw).getTime()) ? new Date() : new Date(raw);

    const normalise = (raw) => {
      if (!raw) return "";
      return raw.includes(IntakeSheetSession.dataBlock)
        ? raw
        : IntakeSheetSession.dataBlock + raw;
    };

    const fullName = [
      patientInfo.patientFirstName,
      patientInfo.patientMiddleName,
      patientInfo.patientLastName,
    ].filter(Boolean).join(" ");

    setPatientName(fullName);
    setFormDate(parseDateSafe(getValue(PregnancyConsentKeys.txtDate)));

    if (isFrontDesk) {
      const savedProvName = getValue(PregnancyConsentKeys.txtProviderName);
      const provider =
        providerOptions.find((x) => x.label === savedProvName) ||
        providerOptions[0] ||
        null;
      const forAddr = providerData.find((x) => x.ProviderId === provider?.value);
      const addr = forAddr
        ? [forAddr.Address, forAddr.City, forAddr.Street, forAddr.State, forAddr.Zip]
            .filter(Boolean).join(", ")
        : "";
      setSelectedProvider(provider);
      setProviderName(provider?.label || "");
      setProviderAddress(addr);
    } else {
      const prov = providerData[0] || {};
      setProviderName(
        getValue(PregnancyConsentKeys.txtProviderName) ||
        prov.ProviderName ||
        tokenData?.providerName ||
        ""
      );
    }

    const savedSig = copySignature
      ? sessionStorage.getItem("sharedPatientSignature")
      : null;
    setPatientSignImg(savedSig || normalise(getValue(PregnancyConsentKeys.signPCPatientSign)));

    setLoading(false);   // ← matches HippaAuth: setLoading(false) inside bindValueToControl
  };

  /* ══════════════════════════════════════════════════
     UPDATE COLLECTION — matches HippaAuth updateCollection exactly
  ══════════════════════════════════════════════════ */
  const updateCollection = (collection) => {
    try {
      const updated = [...collection];
      const set = (key, value) => {
        const item = updated.find((x) => x.Name === key);
        if (item) item.Value = value;
      };
      const fmt = (d) =>
        d == null || d === ""
          ? ""
          : moment(d).format("MM-DD-YYYY").split("-").join("/");

      const resolvedProvName = isFrontDesk
        ? providerOptions.length === 1
          ? providerOptions[0]?.label || ""
          : selectedProvider?.label || ""
        : providerData[0]?.ProviderName || providerName;

      set(PregnancyConsentKeys.txtPatientName,    patientName);
      set(PregnancyConsentKeys.txtDate,           fmt(formDate));
      set(PregnancyConsentKeys.txtProviderName,   resolvedProvName);
      set(PregnancyConsentKeys.signPCPatientSign, patientSignImg);

      return updated;
    } catch (error) {
      console.error(error?.message);
    }
  };

  /* ══════════════════════════════════════════════════
     SAVE API — matches HippaAuth CallSaveApiForIntakeSheet exactly
     (no finally block, loading managed by saveFunction)
  ══════════════════════════════════════════════════ */
  const callSaveApi = async (jsonData) => {
    try {
      const doctorId = isFrontDesk
        ? (providerOptions.length === 1
            ? providerOptions[0]?.value
            : selectedProvider?.value) || ""
        : providerData[0]?.ProviderId || tokenData?.doctorId || "";

      const payload = {
        CaseFormDataId:  CaseFormDataId,   // ← from state, set during callApi
        CaseId:          tokenData.caseId,
        CompanyId:       tokenData.companyId,
        DoctorId:        doctorId,
        EntityLevel:     0,
        FormId:          23,
        FormName:        "Pregnancy Consent",
        FormType:        null,
        IsChanged:       false,
        IsCopySign:      false,
        IsFormCompleted: false,
        UserId:         tokenData?.userId,
        officeId: tokenData?.providerId,
        JsonData: jsonData,
      };

      const response = await SaveCaseFormData(payload);

      if (response.HasException) {
        toast.error("Error while saving Pregnancy Consent.");
        return false;
      } else {
        toast.success("Pregnancy Consent saved successfully.");
        return true;
      }
    } catch {
      toast.error("Error while saving Pregnancy Consent.");
      return false;
    }
  };

  /* ══════════════════════════════════════════════════
     FORM JSON REQUEST — mutates BeforeParsingPC directly,
     same as HippaAuth formJsonRequest
  ══════════════════════════════════════════════════ */
  const formJsonRequest = async (updatedCollection, BeforeParsingData) => {
    try {
      for (let i = 0; i < BeforeParsingData.notesdata.length; i++) {
        const filterDataArr = updatedCollection.filter(
          (x) => x.RenderKeyName === BeforeParsingData.notesdata[i].RenderKeyName
        );
        if (filterDataArr.length > 0) {
          for (let j = 0; j < BeforeParsingData.notesdata[i].ApplicationValue.length; j++) {
            if (
              BeforeParsingData.notesdata[i].ApplicationValue[j].Name === filterDataArr[j]?.Name
            ) {
              BeforeParsingData.notesdata[i].ApplicationValue[j].Type     = filterDataArr[j].Type;
              BeforeParsingData.notesdata[i].ApplicationValue[j].isRender = filterDataArr[j].isRender;

              if (
                BeforeParsingData.notesdata[i].ApplicationValue[j].Name ===
                PregnancyConsentKeys.signPCPatientSign
              ) {
                const val = filterDataArr[j].Value;
                if (val) {
                  if (val.includes(";")) {
                    const block = val.split(";");
                    BeforeParsingData.notesdata[i].ApplicationValue[j].Value =
                      block[1].split(",")[1];
                  } else {
                    BeforeParsingData.notesdata[i].ApplicationValue[j].Value = val;
                  }
                } else {
                  BeforeParsingData.notesdata[i].ApplicationValue[j].Value = "";
                }
              } else {
                BeforeParsingData.notesdata[i].ApplicationValue[j].Value = filterDataArr[j].Value;
              }
            }
          }
        }
      }
      const result = await callSaveApi(JSON.stringify(BeforeParsingData));
      setCollectionOfPC(updatedCollection);
      setBeforeParsingPC(BeforeParsingData);
      return result;
    } catch (error) {
      console.error(error.message);
    }
  };

  /* ══════════════════════════════════════════════════
     SAVE FUNCTION — matches HippaAuth saveFunction exactly
  ══════════════════════════════════════════════════ */
  const saveFunction = async () => {
    try {
      setLoading(true);
      const updatedCollection = updateCollection(collectionOfPC);
      const result = await formJsonRequest(updatedCollection, BeforeParsingPC);
      setLoading(false);
      return result;
    } catch (error) {
      console.error(error?.message);
    }
  };

  /* ══════════════════════════════════════════════════
     SIGNATURE HANDLERS
  ══════════════════════════════════════════════════ */
  const signUpdatedCall = (base64Img) => {
    const newBase = base64Img.includes(IntakeSheetSession.dataBlock1)
      ? base64Img.replace(IntakeSheetSession.dataBlock1, IntakeSheetSession.dataBlock)
      : base64Img;
    setPatientSignImg(newBase);
    sessionStorage.setItem("sharedPatientSignature", newBase);
  };

  const clearSignCall = () => {
    setPatientSignImg("");
    signatureMiss?.(true);
  };

  /* ══════════════════════════════════════════════════
     PROVIDER CHANGE
  ══════════════════════════════════════════════════ */
  const onProviderChange = (prov) => {
    if (!prov) return;
    const forAddr = providerData.find((x) => x.ProviderId === prov.value);
    const addr = forAddr
      ? [forAddr.Address, forAddr.City, forAddr.Street, forAddr.State, forAddr.Zip]
          .filter(Boolean).join(", ")
      : "";
    setSelectedProvider(prov);
    setProviderName(prov.label || "");
    setProviderAddress(addr);
  };

  const ProviderField = () =>
    providerOptions.length === 1 ? (
      <input
        className="pc__input"
        type="text"
        value={providerOptions[0]?.label || ""}
        readOnly
      />
    ) : (
      <Select
        options={providerOptions}
        onChange={onProviderChange}
        value={selectedProvider}
        getOptionLabel={(e) => e?.label}
        getOptionValue={(e) => e?.value}
        tabSelectsValue={false}
        classNamePrefix="react-select"
        placeholder="Select Provider"
      />
    );

  const displayProvider = isFrontDesk
    ? providerOptions.length === 1
      ? providerOptions[0]?.label || ""
      : providerName
    : providerData[0]?.ProviderName || providerName;




  return (
    <>
      {loading ? (
        <SpinnerLoader message="Loading form data…" />
      ) : (
        <div className="pc__wrapper">

          {/* ── Title ── */}
          <div className="pc__title-block">
            <div className="pc__title-main">MRI PREGNANCY CONSENT FORM</div>
          </div>

          {/* ── Header row: Patient Name / Date / Provider ── */}
          <div className="pc__header-grid">
            <div className="pc__header-field">
              <span className="pc__label"><b>Name</b></span>
              <input
                className="pc__input pc__input--grow"
                type="text"
                value={patientName}
                disabled
              />
            </div>

            <div className="pc__header-field">
              <span className="pc__label"><b>Date:</b></span>
              <DatePicker
                selected={formDate}
                onChange={(d) => setFormDate(d)}
                dateFormat="MM/dd/yyyy"
                className="pc__datepicker"
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
              />
            </div>

            <div className="pc__header-field">
              <span className="pc__label"><b>Provider</b></span>
              <div className="pc__provider-field">
                {isFrontDesk ? (
                  <ProviderField />
                ) : (
                  <input
                    className="pc__input pc__input--grow"
                    type="text"
                    value={displayProvider}
                    disabled
                  />
                )}
              </div>
            </div>
          </div>

          {/* ── Legal body ── */}
          <div className="pc__legal-para">
            <b>
              THIS CONSENT IS TO INFORM YOU THAT THE MAGNETIC RESONANCE IMAGING (MRI)
              PROCEDURE YOU ARE HAVING TODAY IS A POSSIBLE RISK TO YOUR UNBORN
              CHILD/FETUS. BY SIGNING THIS YOU ARE CONSENTING TO UNDERSTANDING ALL
              OF THE INFORMATION BELOW AND HAVE ASKED ALL QUESTIONS NEEDED TO
              UNDERSTAND THE RISKS ASSOCIATED WITH THE PROCEDURE. TO DATE,
              THERE ARE NO REPORTS OF INJURY TO CHILDREN WHO UNDERWENT MR IMAGING
              BEFORE BIRTH. WHILE THE NUMBER OF PATIENTS SCANNED DURING PREGNANCY
              IS SMALL, WITH LIMITED FOLLOW-UP, IN THE PAST SEVERAL YEARS,
              NUMEROUS PREGNANT PATIENTS HAVE UNDERGONE MRI WITH NO ILL EFFECTS.
              MR IMAGING OF PREGNANT PATIENTS IS CARRIED OUT WHEN THE PATIENT'S
              PHYSICIAN HAS DECIDED THAT THE ADVANTAGES OF MRI OUTWEIGH THE
              POTENTIAL RISKS.
            </b>
          </div>

          {/* ── I, [name], HAVE READ… ── */}
          <div className="pc__inline-para">
            <b>I,</b>
            <input
              className="pc__input pc__input--inline"
              type="text"
              value={patientName}
              disabled
            />
            <b>
              , HAVE READ THE ABOVE WARNING AND UNDERSTAND THE POTENTIAL HARMFUL
              EFFECTS TO MY UNBORN FETUS. I CONSENT TO HAVE THIS MRI PROCEDURE AS
              PRESCRIBED BY MY PHYSICIAN. I ACKNOWLEDGE THAT I HAVE BEEN GIVEN AMPLE
              OPPORTUNITY TO ASK QUESTIONS AND THAT ALL QUESTIONS HAVE BEEN ANSWERED
              TO MY SATISFACTION.
            </b>
          </div>

          {/* ── FURTHERMORE 1 ── */}
          <div className="pc__inline-para">
            <b>
              FURTHERMORE, I FULLY UNDERSTAND THAT I MAY REFUSE TO HAVE THIS MRI
              PROCEDURE CONDUCTED ON ME WITHOUT ANY OBLIGATION TO
            </b>
            <input
              className="pc__input pc__input--inline"
              type="text"
              value={displayProvider}
              disabled
            />
            <b>
              OR ANY OF ITS SUBSIDIARIES. ALSO, I UNDERSTAND THAT I MAY STOP THIS
              MRI PROCEDURE AT ANY TIME DURING ITS PROCESS.
            </b>
          </div>

          {/* ── FURTHERMORE 2 ── */}
          <div className="pc__inline-para">
            <b>
              FURTHERMORE, I FULLY AGREE THAT THE RISKS DESCRIBED HEREIN ARE RISKS
              THAT I AM WILLING TO ACCEPT. ALSO, I AGREE THAT I WILL HOLD HARMLESS
            </b>
            <input
              className="pc__input pc__input--inline"
              type="text"
              value={displayProvider}
              disabled
            />
            <b>
              AND ANY OF ITS SUBSIDIARIES, OWNERS, AND EMPLOYEES SHOULD I, OR MY
              FETUS EXPERIENCE ANY NEGATIVE EFFECTS FROM THIS MRI PROCEDURE.
            </b>
          </div>

          {/* ── Signature row ── */}
          <div className="pc__sign-row">
            <span className="pc__label"><b>Signature of person giving consent:</b></span>
            <div
              className={`pc__sign-box${!patientSignImg ? " pc__sign-box--empty" : ""}`}
              onClick={() => setSigPadOpen(true)}
            >
              {patientSignImg
                ? <img src={patientSignImg} alt="Patient signature" className="pc__sign-img" />
                : <span className="pc__sign-hint">Click to sign</span>
              }
            </div>
          </div>

          {/* ── Printed name ── */}
          <div className="pc__row">
            <span className="pc__label"><b>Printed Name of person giving consent:</b></span>
            <input
              className="pc__input pc__input--grow"
              type="text"
              value={patientName}
              disabled
            />
          </div>

          {/* ── Signature Pad Modal ── */}
          <SignaturePad
            isOpen={sigPadOpen}
            title="Patient Signature"
            existingSign={patientSignImg}
            onClose={() => setSigPadOpen(false)}
            onSave={(base64) => { signUpdatedCall(base64); setSigPadOpen(false); }}
            onClear={clearSignCall}
          />

        </div>
      )}
    </>
  );
});

export default PregnancyConsent;